import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdBarChart, MdPlayCircle } from 'react-icons/md';
import TestVector from 'assets/vectors/Tests.svg';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import Loader from 'components/Loader';
import Tabs from 'components/Tabs';

import './index.css';

const SUBJECT_ABBR = {
    'Reading and Writing': 'Reading and Writing',
    'Math': 'Math',
    'ACT Reading': 'Reading',
    'ACT Mathematics': 'Math',
    'ACT English': 'English',
    'ACT Science': 'Science',
    'Mathematics': 'Mathematics',
    'English Language Arts': 'English',
    'TSIA2 ELAR': 'ELAR',
    'TSIA2 Mathematics': 'Mathematics'
};

const TEST_URL = {
    'Digital SAT': 'digital-sat',
    'Digital PSAT': 'digital-sat',
    'Digital ACT': 'digital-act',
    'Digital SHSAT': 'digital-shsat',
    'Paper ACT': 'paper-act',
    'TSIA2': 'digital-tsia2',
    'BCA': 'digital-bca',
};

function Tests() {
    const [testTypes, setTestTypes] = useState(null);
    const [testTypeIndex, setTestTypeIndex] = useState(0);
    const [tests, setTests] = useState(null);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);

    useEffect(() => {
        axios.get(`tests/tests/${organization.urlSlug}/`)
            .then(res => {
                // console.log(res.data);
                setTests(res.data.tests);
                let testTypeList = [];
                for (let i = 0; i < res.data.tests.length; i++) {
                    if (!testTypeList.includes(res.data.tests[i].testType)) {
                        testTypeList.push(res.data.tests[i].testType);
                    }
                }
                setTestTypes(testTypeList);
            })
            .catch(error => {
                dispatch(setError(error));
            });
    }, []);

    if (tests === null) return <Loader />;
    else if (tests.length === 0) {
        return (
            <div className='layout'>
                <Helmet>
                    <title>
                        Tests - {organization.name}
                    </title>
                </Helmet>
                <h4 className='tests2__subHeader'>No tests assigned</h4>
            </div>
        );
    }

    const assigned = tests.filter(test => (
        test.testType === testTypes[testTypeIndex] &&
        !test.isStarted
    ));
    const inProgress = tests.filter(test => (
        test.testType === testTypes[testTypeIndex] &&
        test.isStarted && !test.testDate
    ));
    const completed = tests.filter(test => (
        test.testType === testTypes[testTypeIndex] &&
        test.isStarted && test.testDate
    ));

    // Completion % for the in-progress bar. Prefer question-level progress
    // (answeredCount / questionCount) so a partially-answered section reads
    // accurately — fixed-form SHSAT has one big module per section, so the
    // old module-level (rawScore) metric could only show 0/50/100%. Falls
    // back to module-level when question counts aren't provided (e.g. adaptive).
    const progressPct = (test) => {
        const sections = test.testSections || [];
        const answered = sections.reduce((sum, s) => sum + (s.answeredCount || 0), 0);
        const totalQuestions = sections.reduce((sum, s) => sum + (s.questionCount || 0), 0);
        if (totalQuestions > 0) return Math.round(100 * answered / totalQuestions);
        const modulesDone = sections.reduce((sum, s) => sum + s.testModules.filter(m => m.rawScore !== null).length, 0);
        const modulesTotal = sections.reduce((sum, s) => sum + s.numModules, 0);
        return modulesTotal > 0 ? Math.round(100 * modulesDone / modulesTotal) : 0;
    };

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    Tests - {organization.name}
                </title>
            </Helmet>
            <div className='tests2'>
                <Tabs
                    className='tests2__tabs'
                    tabs={testTypes}
                    tabIndex={testTypeIndex}
                    onSelect={testTypes.map((_testType, t) => (() => setTestTypeIndex(t)))}
                />
                {inProgress.length > 0 && <h4 className='tests2__subHeader'>In progress</h4>}
                {inProgress.map(test => (
                    <button
                        className='tests2__test'
                        onClick={() => navigate(`/test/${TEST_URL[test.testType]}/${test.testId}`)}
                        key={test.testId}
                    >
                        <img className='tests2__testVector' src={TestVector} />
                        <div className='tests2__info'>
                            <h2 className='tests2__name'>{test.name}</h2>
                            <div className='tests2__progressBar'>
                                <span style={{ width: `${progressPct(test)}%` }}></span>
                            </div>
                            <div className='tests2__progress'>
                                <p>{test.testSections.length === test.numTestSections
                                    ? 'Full Test'
                                    : test.testSections.length === 1
                                    ? `${SUBJECT_ABBR[test.testSections[0].subject]} Only`
                                    : test.testSections.map(section => SUBJECT_ABBR[section.subject]).join(', ')
                                }</p>
                                <p>
                                    {progressPct(test)}% complete
                                </p>
                                {/* <p>resume {test.testSections.find(testSection => (
                                        testSection.score === null
                                    )).testModules.find(testModule => (
                                        testModule.rawScore === null
                                    )).name}
                                </p> */}
                            </div>
                        </div>
                        <MdPlayCircle className='tests2__statusIcon' />
                    </button>
                ))}
                {assigned.length > 0 && <h4 className='tests2__subHeader'>Not started</h4>}
                {assigned.map(test => (
                    <button
                        className='tests2__test'
                        onClick={() => navigate(`/test/${TEST_URL[test.testType]}/${test.testId}`)}
                        key={test.testId}
                    >
                        <img className='tests2__testVector' src={TestVector} />
                        <div className='tests2__info'>
                            <h2 className='tests2__name'>{test.name}</h2>
                            <div className='tests2__progressBar'>
                                <span style={{ width: '0%' }}></span>
                            </div>
                            <div className='tests2__progress'>
                                <p>{test.testSections.length === test.numTestSections
                                    ? 'Full Test'
                                    : test.testSections.length === 1
                                    ? `${SUBJECT_ABBR[test.testSections[0].subject]} Only`
                                    : test.testSections.map(section => SUBJECT_ABBR[section.subject]).join(', ')
                                }</p>
                                <p>
                                    assigned on {new Date(test.createdAt).toLocaleString('en-us', { year: 'numeric', month: 'numeric', day: 'numeric' })}
                                </p>
                            </div>
                        </div>
                        <MdPlayCircle className='tests2__statusIcon' />
                    </button>
                ))}
                {completed.length > 0 && <h4 className='tests2__subHeader'>Completed</h4>}
                {completed.map(test => (
                    <button
                        className='tests2__test'
                        onClick={() => navigate(`/report/${TEST_URL[test.testType]}/${test.testId}`)}
                        key={test.testId}
                    >
                        <img className='tests2__testVector' src={TestVector} />
                        <div className='tests2__info'>
                            <h2 className='tests2__name'>{test.name}</h2>
                            <div className='tests2__progressBar'>
                                <span style={{ width: '100%' }}></span>
                            </div>
                            <div className='tests2__progress'>
                                <p>{test.testSections.length === test.numTestSections
                                    ? 'Full Test'
                                    : test.testSections.length === 1
                                    ? `${SUBJECT_ABBR[test.testSections[0].subject]} Only`
                                    : test.testSections.map(section => SUBJECT_ABBR[section.subject]).join(', ')
                                }</p>
                                <p>{new Date(test.testDate).toLocaleString('en-us', { year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                            </div>
                        </div>
                        <MdBarChart className='tests2__statusIcon' />
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Tests;